import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../stores/authStore";

export type RecoveryIntent = {
  kind: "merge" | "split";
  id: string;
  digest: string;
  traderId: string;
  conditionId: string;
  amountAtomic: string;
  state: string;
  requestId: string | null;
  transactionHash: string | null;
  canAbandon: boolean;
  canReconcile?: boolean;
  canAccount?: boolean;
};
export type RecoveryPreview = {
  status: "recorded" | "not_enrolled" | "unavailable";
  generation: number | null;
  mergeEnabled: boolean;
  splitEnabled: boolean;
  intents: RecoveryIntent[];
};
function amount(raw: string) {
  const n = BigInt(raw);
  return `${n / 1_000_000n}${n % 1_000_000n ? "." + (n % 1_000_000n).toString().padStart(6, "0").replace(/0+$/, "") : ""}`;
}
/** Explicit recovery only; never submits a chain transaction. Account changes unmount this component; no retries. */
export function MergeRecoveryControls({
  target,
  preview,
  onPending,
  onFinished,
}: {
  target: { orgId: string; credentialId: string; wallet: string };
  preview: RecoveryPreview;
  onPending: () => void;
  onFinished: (notice: string) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const request = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current?.abort();
    };
  }, []);
  const row = preview.intents.find((i) => i.id === selected);
  const eligible = (i: RecoveryIntent) =>
    preview.status === "recorded" &&
    Number.isSafeInteger(preview.generation) &&
    preview.generation! >= 0 &&
    i.canAbandon &&
    i.state === "reserved" &&
    i.requestId === null &&
    i.transactionHash === null;
  const actionFor = (i: RecoveryIntent) => {
    if (eligible(i))
      return i.kind === "split" ? "abandon_unsent_split" : "abandon_unsent";
    if (
      preview.status !== "recorded" ||
      !Number.isSafeInteger(preview.generation) ||
      preview.generation! < 0 ||
      i.kind !== "split" ||
      (i.transactionHash !== null &&
        !/^0x[\da-fA-F]{64}$/.test(i.transactionHash))
    )
      return null;
    if (i.canAccount && i.state === "confirmed" && i.transactionHash !== null)
      return "account_split";
    if (
      i.canReconcile &&
      ["dispatching", "submitted", "unknown"].includes(i.state)
    )
      return "reconcile_split";
    return null;
  };
  const action = row ? actionFor(row) : null;
  const label = (value: string | null) =>
    value === "account_split"
      ? "Account confirmed split"
      : value === "reconcile_split"
        ? "Verify split receipt"
        : "Release unsent reservation";
  async function release() {
    if (request.current || !row || !action || confirm !== row.id) return;
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    onPending();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    let notice =
      "Recovery outcome is unconfirmed. Review the refreshed intent before trying again; no automatic retry was made.";
    try {
      const res = await apiFetch(
        "/api/admin/poly-merge-recovery",
        {
          method: "POST",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...target,
            action,
            intentId: row.id,
            ...(row.kind === "split" ? { digest: row.digest } : {}),
            generation: preview.generation,
            confirm,
          }),
        },
        { retryUnauthorized: false },
      );
      if (res.ok) {
        const receipt = await res.json();
        if (
          receipt.intentId === row.id &&
          receipt.digest === row.digest &&
          receipt.wallet === target.wallet &&
          receipt.executionEnabled === false
        ) {
          if (action === "reconcile_split" && receipt.kind === action) {
            if (receipt.status === "held" && receipt.outcome === "conflict")
              notice =
                "Split evidence conflicts with the recorded operation. Both execution opt-ins are paused; the cash hold remains. Review the conflict before any further action.";
            else if (receipt.status === "held")
              notice =
                "Split remains held. The receipt did not establish a finalized split. No accounting or retry was performed; review the refreshed state.";
            else if (
              receipt.status === "abandoned_with_proof" &&
              receipt.generation === preview.generation! + 1
            )
              notice =
                "Split did not execute: finalized chain evidence proved the nonce unused after expiry. The cash hold was released; the daily allowance was not refunded. Both execution opt-ins remain off.";
            else if (
              (receipt.status === "confirmed" &&
                receipt.generation === preview.generation! + 1) ||
              (receipt.status === "already_confirmed" &&
                receipt.generation === preview.generation)
            )
              notice =
                "Split receipt verified. Cash remains reserved until you separately review and account it. Both execution opt-ins remain off.";
          } else if (action === "account_split" && receipt.kind === action) {
            if (
              (receipt.status === "accounted" &&
                receipt.generation === preview.generation! + 1) ||
              (receipt.status === "already_accounted" &&
                receipt.generation === preview.generation)
            )
              notice =
                "Split accounted: both position legs and their costs were reconciled, and the reservation was released. Both execution opt-ins remain off.";
          } else if (receipt.generation === preview.generation! + 1)
            notice =
              "Unsent reservation released. No transaction was submitted. Read the refreshed state below.";
        }
      } else if (res.status === 400) {
        notice =
          "Recovery request was refused as invalid. Review the refreshed state before trying again.";
      } else if (res.status === 409) {
        notice =
          "Recovery refused. The reservation may have changed or been dispatched. Review the refreshed state; no automatic retry was made.";
      } else if (res.status === 401 || res.status === 403) {
        notice =
          "Recovery permission was refused. No automatic retry was made.";
      }
    } catch {
      /* A lost response cannot prove whether the local write completed. */
    }
    // Keep this snapshot spent. Parent clears it before refreshing; reusing it
    // would reopen an action after an ambiguous response.
    clearTimeout(timeout);
    if (mounted.current) onFinished(notice);
  }
  if (preview.status !== "recorded")
    return (
      <section
        aria-label="Recorded operations"
        className="border-border mt-4 border-t pt-3"
      >
        <h4 className="font-semibold">Recorded operations</h4>
        <p className="text-muted-foreground mt-1">
          {preview.status === "not_enrolled"
            ? "No wallet enrollment or recorded operations. Reading this preview does not create one."
            : "Recorded operation evidence is unavailable. Existing holds remain protected; recovery controls are unavailable."}
        </p>
      </section>
    );
  return (
    <section
      aria-label="Recorded operations"
      className="border-border mt-4 min-w-0 border-t pt-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-semibold">Recorded operations</h4>
        <span className="text-muted-foreground">
          Wallet revision {preview.generation}
        </span>
      </div>
      {(preview.mergeEnabled || preview.splitEnabled) && (
        <p role="alert" className="mt-2 text-amber-700 dark:text-amber-400">
          Stored opt-in is on for {preview.mergeEnabled ? "merges" : ""}
          {preview.mergeEnabled && preview.splitEnabled ? " and " : ""}
          {preview.splitEnabled ? "splits" : ""}. Activation is unavailable
          here. Split confirmation and accounting pause both opt-ins.
        </p>
      )}
      <p className="text-muted-foreground my-2">
        Release only reservations that never reached dispatch. Submitted and
        uncertain splits can be verified even when the response was lost:
        verification looks up the recorded wallet nonce and checks the full
        receipt. Confirmed splits require a separate accounting review.
        Verification never retries dispatch. A missing receipt or elapsed
        deadline alone does not release a hold. Finalized proof that the nonce
        was unused after expiry can release it without refunding daily usage.
      </p>
      {preview.intents.length === 0 ? (
        <p>No recorded operations.</p>
      ) : (
        <div className="border-border max-h-[28rem] overflow-auto rounded border">
          <table className="w-full min-w-[640px] table-fixed text-left">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="w-[19%] p-2">Operation / state</th>
                <th className="w-[43%] p-2">Identity</th>
                <th className="w-[16%] p-2">Reserved amount</th>
                <th className="w-[22%] p-2">Recovery</th>
              </tr>
            </thead>
            <tbody>
              {preview.intents.map((i) => (
                <tr key={i.id} className="border-border border-t align-top">
                  <td className="p-2">
                    <span className="capitalize">{i.kind}</span>
                    <div className="text-muted-foreground">{i.state}</div>
                  </td>
                  <td className="space-y-1 break-all p-2">
                    <div>Intent {i.id}</div>
                    <div className="text-muted-foreground">
                      Condition {i.conditionId}
                    </div>
                    <div className="text-muted-foreground">
                      Trader {i.traderId}
                    </div>
                    {i.requestId && (
                      <div className="text-muted-foreground">
                        Request {i.requestId}
                      </div>
                    )}
                    {i.transactionHash && (
                      <div className="text-muted-foreground">
                        Transaction {i.transactionHash}
                      </div>
                    )}
                  </td>
                  <td className="break-words p-2">
                    {amount(i.amountAtomic)}{" "}
                    {i.kind === "split" ? "pUSD" : "YES + NO pairs"}
                  </td>
                  <td className="p-2">
                    {actionFor(i) ? (
                      <button
                        type="button"
                        className="border-border rounded border px-2 py-1 disabled:opacity-50"
                        aria-label={`${eligible(i) ? "Review release" : label(actionFor(i))} ${i.id}`}
                        disabled={pending}
                        onClick={() => {
                          setSelected(i.id);
                          setConfirm("");
                        }}
                      >
                        {eligible(i) ? "Review release" : label(actionFor(i))}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">
                        {["accounted", "abandoned"].includes(i.state)
                          ? "No active hold"
                          : "Hold retained · reconciliation required"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {row && (
        <div className="border-border mt-3 space-y-2 border-t pt-3">
          <p>
            {label(action)} for <strong className="break-all">{row.id}</strong>?
            {action === "account_split"
              ? " This records both acquired legs and releases the cash reservation atomically. It does not reverse the original merge profit."
              : action === "reconcile_split"
                ? " This checks finalized chain evidence. A confirmed split keeps its cash reserved until accounting; proof that it never executed after expiry releases the hold. It never submits a transaction or refunds daily usage."
                : " This does not reverse a transaction or refund daily usage."}{" "}
            If the response is lost, refresh and review before another action.
          </p>
          <label className="block">
            Type the exact intent ID
            <input
              className="bg-background border-border mt-1 block w-full max-w-lg rounded border p-2"
              value={confirm}
              disabled={pending}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="border-border rounded border px-3 py-2 disabled:opacity-50"
              disabled={pending || confirm !== row.id || !action}
              onClick={() => void release()}
            >
              {pending ? "Checking…" : label(action)}
            </button>
            <button
              type="button"
              className="border-border rounded border px-3 py-2 disabled:opacity-50"
              disabled={pending}
              onClick={() => {
                setSelected(null);
                setConfirm("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
